// Lureo's design tokens: every CSS property the app's look is drawn with, named lureo- and what it is for, in groups
// (TOKEN_GROUPS: surfaces, ink, brand, status, the palette, components, shadows, shape, motion, layers, type and more). A
// fill is a noun, marks on it are on-<fill>, and the same colour as marks on the page is <colour>-ink, with -subtlest,
// -subtle and -strong steps.
// A token set (a theme people pick: a row of the themes table, or Lureo's own Light and Dark in seed/themes.json) holds a
// plain value for every token, all but Dark, which is 1 in a dark set and 0 in a light one. The app puts a set's values on
// the page as they are. Some tokens have a formula (`derive`) that makes a value from the set's others, the large corner
// from the roundness: only making a set uses it (fill.ts), and a set's values never follow one another. A colour is a whole
// CSS colour, such as oklch(0.42 0.08 339), read as var(--lureo-brand).

export type TokenKind =
  | 'colour' // a whole CSS colour, oklch(L C H)
  | 'gradient'
  | 'shadow'
  | 'length'
  | 'font' // a font-family stack
  | 'pattern' // the texture's pattern
  | 'amount' // 0 to 1
  | 'logo' // 'artwork' or 'shape'
  | 'duration' // a time, 150ms
  | 'easing' // a curve, cubic-bezier(x1, y1, x2, y2) with x1 and x2 from 0 to 1, or steps(4, jump-end) for frame-by-frame motion
  | 'layer' // a z-index: a whole number, and what has the higher one sits over the other
  | 'weight' // a font weight, 100 to 900
  | 'case' // a label's case: none, uppercase or lowercase
  | 'features' // OpenType features, as font-feature-settings: normal, or "ss03", "cv01"
  | 'angle' // a gradient's direction: to bottom right, or an angle such as 135deg
  | 'width' // a font's width, as font-stretch takes it: 100% is normal, 125% wider, 75% narrower
  | 'decoration' // a line through or under text, as text-decoration takes it: underline, none, underline dashed 2px
  | 'scale' // a size multiplier: 1 is unchanged, 0.975 a little smaller, 1.02 a little larger
  | 'style' // a font style: normal or italic
  | 'line'; // a line's style: solid, dashed, dotted or double

/** How far a mix goes: a fraction, one for a light theme and one for a dark one, or an amount token's value. */
export type MixAmount = number | readonly [light: number, dark: number] | string;

/**
 * A formula: how a token's value is made from others, for a new set: the same value; a mix in OKLab; a colour at an opacity (offset + opacity ×
 * an amount); a colour's OKLCH channels moved by an amount (the lightness the other way in a dark theme when `flips`); a
 * fixed value; one way in a light theme and another in a dark one; the theme's darkness from its mode; or CSS.
 */
export type Derive =
  | { alias: string }
  | { mix: [from: string, to: string, amount: MixAmount] }
  | { alpha: [colour: string, opacity: number, scaledBy: string | null, offset?: number] }
  | { shift: [colour: string, by: { l?: number; c?: number; h?: number }, scaledBy: string | null, flips: boolean] }
  | { fixed: string }
  | { modes: [light: Derive, dark: Derive] }
  | { mode: true }
  | { css: string };

export interface TokenSpec {
  name: string;
  label: string;
  kind: TokenKind;
  /** Its formula, for a new set's value; none for a token each set chooses. */
  derive?: Derive;
}

export interface TokenGroup {
  id: string;
  label: string;
  describes: string;
  tokens: readonly TokenSpec[];
}

const t = (name: string, label: string, kind: TokenKind, derive?: Derive): TokenSpec => (derive ? { name, label, kind, derive } : { name, label, kind });

export const TOKEN_GROUPS: readonly TokenGroup[] = [
  {
    id: "mode",
    label: "Mode",
    describes: "Whether the set is dark: 1 for a dark set, 0 for a light one, from the set's mode, set by hand",
    tokens: [
      t("lureo-dark", "Dark", "amount", { mode: true }),
    ],
  },
  {
    id: "surfaces",
    label: "Surfaces",
    describes: "The backgrounds. The surface is the colour of cards, fields, menus and panels; the page sits a step darker, and the neutral fills, the backdrop and header bars are steps from the surface; the editor chrome is the fill of the editor's bars floating over the 3D view (see-through for glass, frosted at the large blur). The scrim dims the page behind a dialog",
    tokens: [
      t("lureo-surface", "Surface", "colour"),
      t("lureo-page", "Page", "colour", { mix: ["lureo-surface", "lureo-shade", [0.038, 0.199]] }),
      t("lureo-neutral-subtle", "Neutral subtle", "colour", { mix: ["lureo-surface", "lureo-shade", [0.015, 0.097]] }),
      t("lureo-neutral", "Neutral", "colour", { mix: ["lureo-surface", "lureo-ink-strong", [0.043, 0.056]] }),
      t("lureo-neutral-strong", "Neutral strong", "colour", { mix: ["lureo-surface", "lureo-ink-strong", [0.108, 0.138]] }),
      t("lureo-thumb", "Thumbnail", "colour", { mix: ["lureo-surface", "lureo-ink-strong", [0.043, 0.056]] }),
      t("lureo-thumb-strong", "Thumbnail strong", "colour", { mix: ["lureo-surface", "lureo-ink-strong", [0.108, 0.138]] }),
      t("lureo-meter-empty", "Meter empty", "colour", { mix: ["lureo-surface", "lureo-ink-strong", [0.16, 0.3]] }),
      t("lureo-backdrop-from", "Backdrop from", "colour", { mix: ["lureo-surface", "lureo-ink-strong", [0.025, 0.042]] }),
      t("lureo-chrome", "Editor chrome", "gradient", { alias: "lureo-backdrop" }),
      t("lureo-editor-bar", "Editor top bar", "gradient", { alias: "lureo-chrome" }),
      t("lureo-menu", "Menu", "colour", { alias: "lureo-surface" }),
      t("lureo-dialog", "Dialog", "colour", { alias: "lureo-page" }),
      t("lureo-popover", "Pop-up", "colour", { alias: "lureo-menu" }),
      t("lureo-on-menu", "On menu", "colour", { alias: "lureo-ink" }),
      t("lureo-on-menu-subtle", "On menu, subtle", "colour", { alias: "lureo-ink-subtle" }),
      t("lureo-on-menu-subtlest", "On menu, subtlest", "colour", { alias: "lureo-ink-subtlest" }),
      t("lureo-menu-hover", "Menu row hover", "colour", { alias: "lureo-hover" }),
      t("lureo-on-menu-hover", "On menu row hover", "colour", { alias: "lureo-on-menu" }),
      t("lureo-menu-line", "Menu line", "colour", { alias: "lureo-line" }),
      t("lureo-bar-line", "Header bar line", "colour", { alpha: ["lureo-highlight", 0.3, null] }),
      t("lureo-backdrop", "Backdrop", "gradient", { css: "linear-gradient(135deg, var(--lureo-backdrop-from), color-mix(in oklab, var(--lureo-surface), var(--lureo-shade) calc(4% + 0.8% * var(--lureo-dark))))" }),
      t("lureo-bar", "Header bar", "colour", { alpha: ["lureo-surface", -0.8, "lureo-glass", 1] }),
      t("lureo-scrim", "Scrim", "colour", { alpha: ["lureo-shade", 0.8, null] }),
    ],
  },
  {
    id: "ink",
    label: "Ink",
    describes: "Text and icons. The theme picks the strongest ink; the softer inks are steps from it toward the surface. Also the text on dark fills, and its softer steps, the inks of text that sits straight on the page, and the ink of text and icons on header bars",
    tokens: [
      t("lureo-ink-strong", "Ink strong", "colour"),
      t("lureo-ink", "Ink", "colour", { mix: ["lureo-surface", "lureo-ink-strong", [0.95, 0.878]] }),
      t("lureo-ink-subtle", "Ink subtle", "colour", { mix: ["lureo-surface", "lureo-ink-strong", [0.82, 0.768]] }),
      t("lureo-ink-subtlest", "Ink subtlest", "colour", { mix: ["lureo-surface", "lureo-ink-strong", [0.388, 0.444]] }),
      t("lureo-page-ink-strong", "Page ink strong", "colour", { alias: "lureo-ink-strong" }),
      t("lureo-page-ink", "Page ink", "colour", { alias: "lureo-ink" }),
      t("lureo-page-ink-subtle", "Page ink subtle", "colour", { alias: "lureo-ink-subtle" }),
      t("lureo-page-ink-subtlest", "Page ink subtlest", "colour", { alias: "lureo-ink-subtlest" }),
      t("lureo-on-emphasis", "On emphasis", "colour"),
      t("lureo-on-emphasis-subtle", "On emphasis subtle", "colour", { mix: ["lureo-on-emphasis", "lureo-emphasis", 0.07] }),
      t("lureo-on-emphasis-subtlest", "On emphasis subtlest", "colour", { mix: ["lureo-on-emphasis", "lureo-emphasis", 0.36] }),
      t("lureo-bar-ink", "Header bar ink", "colour", { alias: "lureo-ink-strong" }),
    ],
  },
  {
    id: "lines",
    label: "Lines",
    describes: "Hairlines and the stronger lines around frames and fields (steps from the surface toward the strongest ink), a card's edge (the line on a solid theme, fading into the card as the theme turns to glass, where its shadow draws the edge), and the white rim light catches on a surface (faint on a solid dark theme)",
    tokens: [
      t("lureo-line", "Line", "colour", { mix: ["lureo-surface", "lureo-ink-strong", [0.098, 0.125]] }),
      t("lureo-line-strong", "Line strong", "colour", { mix: ["lureo-surface", "lureo-ink-strong", [0.173, 0.228]] }),
      t("lureo-card-line", "Card line", "colour", { mix: ["lureo-line", "lureo-surface", "lureo-glass"] }),
      t("lureo-highlight", "Highlight", "colour", { fixed: "oklch(1 0 0)" }),
    ],
  },
  {
    id: "states",
    label: "States",
    describes: "The wash behind something hovered, a pressed link, a danger button's hover and pressed colours, how faded a disabled control is (or, for a set with a disabled fill, the fill and text a disabled button takes instead), and the focus mark a keyboard user sees: its colour, how thick it is, how far outside the control it sits, and a ring or halo drawn with it",
    tokens: [
      t("lureo-hover", "Hover", "colour", { alpha: ["lureo-brand-ink", 0.06, null] }),
      t("lureo-brand-ink-pressed", "Brand ink pressed", "colour", { mix: ["lureo-brand-ink", "lureo-ink-strong", 0.31] }),
      t("lureo-danger-hover", "Danger hover", "colour", { mix: ["lureo-danger", "lureo-shade", 0.1] }),
      t("lureo-danger-pressed", "Danger pressed", "colour", { mix: ["lureo-danger", "lureo-shade", 0.2] }),
      t("lureo-disabled-opacity", "Disabled opacity", "amount", { fixed: "0.5" }),
      t("lureo-focus", "Focus", "colour", { alias: "lureo-brand-ink" }),
      t("lureo-focus-width", "Focus width", "length", { fixed: "2px" }),
      t("lureo-focus-offset", "Focus offset", "length", { fixed: "2px" }),
      t("lureo-focus-ring", "Focus ring", "shadow", { fixed: "0 0 #0000" }),
      t("lureo-focus-style", "Focus line style", "line", { fixed: "solid" }),
      t("lureo-disabled-fill", "Disabled fill", "gradient", { fixed: "none" }),
      t("lureo-on-disabled", "On disabled", "colour", { alias: "lureo-ink-subtlest" }),
    ],
  },
  {
    id: "brand",
    label: "Brand",
    describes: "Lureo's berry, as a fill and as text, and the text and icons on the brand fill; the ink of links (on panels, and on the page where a set's page and panels need different inks), the line under them, and their ink and any highlight behind them under the pointer; the secondary purple and the text on it (solid buttons), and the colour laid over solid buttons and their text under the pointer and while pressed; the bright accent; and the deep fills behind the hero band, the loading screen, the featured plan and the dimmed backdrop",
    tokens: [
      t("lureo-brand", "Brand", "colour"),
      t("lureo-brand-ink", "Brand ink", "colour"),
      t("lureo-on-brand-ink", "On brand ink", "colour", { alias: "lureo-on-emphasis" }),
      t("lureo-on-brand", "On brand", "colour"),
      t("lureo-link", "Link", "colour"),
      t("lureo-page-link", "Page link", "colour", { alias: "lureo-link" }),
      t("lureo-link-decoration", "Link line", "decoration", { fixed: "underline" }),
      t("lureo-link-decoration-hover", "Link line on hover", "decoration", { fixed: "underline" }),
      t("lureo-link-underline-offset", "Link line gap", "length", { fixed: "2px" }),
      t("lureo-link-visited", "Visited link", "colour", { alias: "lureo-link" }),
      t("lureo-link-pressed", "Pressed link", "colour", { alias: "lureo-link-hover" }),
      t("lureo-link-hover", "Link hover", "colour", { alpha: ["lureo-link", 0.8, null] }),
      t("lureo-page-link-hover", "Page link hover", "colour", { alias: "lureo-link-hover" }),
      t("lureo-link-hover-wash", "Link hover wash", "shadow", { fixed: "0 0 #0000" }),
      t("lureo-secondary", "Secondary", "colour"),
      t("lureo-on-secondary", "On secondary", "colour", { alias: "lureo-on-emphasis" }),
      t("lureo-secondary-hover", "Secondary hover", "colour", { alpha: ["lureo-emphasis", 0.05, null] }),
      t("lureo-secondary-pressed", "Secondary pressed", "colour", { alpha: ["lureo-emphasis", 0.15, null] }),
      t("lureo-on-secondary-hover", "On secondary hover", "colour", { alias: "lureo-on-secondary" }),
      t("lureo-on-secondary-pressed", "On secondary pressed", "colour", { alias: "lureo-on-secondary" }),
      t("lureo-accent", "Accent", "colour"),
      t("lureo-emphasis", "Emphasis", "colour"),
      t("lureo-emphasis-strong", "Emphasis strong", "colour"),
      t("lureo-glyph", "Form glyph", "colour", { alias: "lureo-emphasis" }),
      t("lureo-button-ink", "Button label", "colour", { alias: "lureo-ink" }),
      t("lureo-emphasis-ink", "Emphasis ink", "colour", { alias: "lureo-emphasis" }),
    ],
  },
  {
    id: "status",
    label: "Status",
    describes: "Information, success, warning and danger: each a fill, and an ink for text and icons on the page",
    tokens: [
      t("lureo-success", "Success", "colour"),
      t("lureo-success-ink", "Success ink", "colour"),
      t("lureo-warning", "Warning", "colour"),
      t("lureo-warning-ink", "Warning ink", "colour"),
      t("lureo-danger", "Danger", "colour"),
      t("lureo-danger-ink", "Danger ink", "colour"),
    ],
  },
  {
    id: "palette",
    label: "Palette",
    describes: "The colours for tags, categories, markers and triggers. Each bright colour has an ink for text on the page. Also the light grey, and the black shadows are drawn in",
    tokens: [
      t("lureo-aqua", "Aqua", "colour"),
      t("lureo-aqua-ink", "Aqua ink", "colour"),
      t("lureo-teal", "Teal", "colour"),
      t("lureo-signal-strong", "Strong signal", "colour", { alias: "lureo-teal" }),
      t("lureo-mint", "Mint", "colour"),
      t("lureo-mint-ink", "Mint ink", "colour"),
      t("lureo-berry-ink", "Berry ink", "colour", { alias: "lureo-emphasis-strong" }),
      t("lureo-on-berry-ink", "On berry ink", "colour", { alias: "lureo-on-emphasis" }),
      t("lureo-coral", "Coral", "colour"),
      t("lureo-coral-ink", "Coral ink", "colour"),
      t("lureo-sky", "Sky", "colour"),
      t("lureo-sky-ink", "Sky ink", "colour"),
      t("lureo-lavender", "Lavender", "colour"),
      t("lureo-lavender-ink", "Lavender ink", "colour"),
      t("lureo-violet", "Violet", "colour"),
      t("lureo-on-violet", "On violet", "colour", { alias: "lureo-on-emphasis" }),
      t("lureo-grey-strong", "Grey strong", "colour"),
      t("lureo-grey", "Grey", "colour", { mix: ["lureo-grey-strong", "lureo-highlight", 0.51] }),
      t("lureo-shade", "Shade", "colour", { fixed: "oklch(0 0 0)" }),
    ],
  },
  {
    id: "cta",
    label: "Call to action",
    describes: "New Story and Publish, the main action: its colour (Lureo's is the bright accent), the text on it, its gradient, the colour laid over it under the pointer and while pressed (a wash, or the brand's own hover fill), and the soft button, the text on it and the colour laid over it under the pointer and while pressed; the call to action's text under the pointer and while pressed",
    tokens: [
      t("lureo-cta", "Call to action", "colour"),
      t("lureo-cta-to", "To", "colour", { shift: ["lureo-cta", { l: -0.1597, c: -0.0361, h: 46.94 }, "lureo-sheen", false] }),
      t("lureo-cta-angle", "Call to action direction", "angle", { fixed: "to bottom right" }),
      t("lureo-cta-fill", "Call to action fill", "gradient", { fixed: "none" }),
      t("lureo-on-cta", "On the call to action", "colour"),
      t("lureo-on-cta-soft", "On the soft button", "colour", { alias: "lureo-on-cta" }),
      t("lureo-cta-hover", "Hover", "colour", { alpha: ["lureo-ink-subtlest", 0.1, null] }),
      t("lureo-cta-pressed", "Pressed", "colour", { alpha: ["lureo-ink-subtlest", 0.35, null] }),
      t("lureo-on-cta-hover", "On hover", "colour", { mix: ["lureo-on-cta", "lureo-cta-to", 0.47] }),
      t("lureo-on-cta-pressed", "On pressed", "colour", { mix: ["lureo-on-cta", "lureo-cta-to", 0.32] }),
      t("lureo-cta-label-hover", "Label on hover", "colour", { alias: "lureo-on-cta" }),
      t("lureo-cta-label-pressed", "Label on pressed", "colour", { alias: "lureo-on-cta" }),
      t("lureo-cta-soft-from", "Soft from", "colour", { mix: ["lureo-cta", "lureo-on-emphasis", 0.2] }),
      t("lureo-cta-soft-to", "Soft to", "colour", { mix: ["lureo-cta", "lureo-shade", 0.07] }),
      t("lureo-cta-soft-hover", "Soft hover", "colour", { alpha: ["lureo-emphasis", 0.05, null] }),
      t("lureo-cta-soft-pressed", "Soft pressed", "colour", { alpha: ["lureo-emphasis", 0.15, null] }),
    ],
  },
  {
    id: "controls",
    label: "Controls",
    describes: "Chips' fill, text and line, unchosen and chosen, and an unchosen chip's under the pointer; an outline button's wash, text and line under the pointer and while pressed; a card's line under the pointer; the wash behind the open tab and the text on it; the tray behind a segmented control's options; a switch's track when it is on; a selected tile or option's fill, text and line; an outlined tag's line and its weight; and the inset boxes in panels and forms (notes, tips, read-more boxes): their fill, and their line's colour and weight (four widths draw it on chosen sides: 0 0 0 2px is a rule down the left)",
    tokens: [
      t("lureo-chip", "Chip", "colour", { alias: "lureo-surface" }),
      t("lureo-on-chip", "On chip", "colour", { alias: "lureo-ink" }),
      t("lureo-chip-line", "Chip line", "colour", { alias: "lureo-line" }),
      t("lureo-chip-on", "Chosen chip", "colour", { alpha: ["lureo-brand-ink", 0.1, null] }),
      t("lureo-on-chip-on", "On chosen chip", "colour", { alias: "lureo-brand-ink" }),
      t("lureo-chip-on-line", "Chosen chip line", "colour", { alias: "lureo-brand-ink" }),
      t("lureo-chip-hover", "Chip hover", "colour", { alias: "lureo-chip" }),
      t("lureo-on-chip-hover", "On chip hover", "colour", { alias: "lureo-on-chip" }),
      t("lureo-chip-hover-line", "Chip hover line", "colour", { alias: "lureo-brand-ink" }),
      t("lureo-chip-on-hover", "Chosen chip hover", "colour", { alias: "lureo-chip-on" }),
      t("lureo-chip-line-width", "Chip line weight", "length", { alias: "lureo-border-width-medium" }),
      t("lureo-upload-line-width", "Upload frame weight", "length", { alias: "lureo-border-width-strong" }),
      t("lureo-outline-hover", "Outline button hover", "colour", { alpha: ["lureo-emphasis", 0.1, null] }),
      t("lureo-outline-pressed", "Outline button pressed", "colour", { alpha: ["lureo-emphasis", 0.2, null] }),
      t("lureo-on-outline-hover", "On outline button hover", "colour", { alias: "lureo-brand-ink" }),
      t("lureo-outline-line-hover", "Outline button hover line", "colour", { alias: "lureo-brand-ink" }),
      t("lureo-card-hover-line", "Card hover line", "colour", { alias: "lureo-brand-ink" }),
      t("lureo-card-hover", "Card hover", "colour", { alias: "lureo-surface" }),
      t("lureo-field", "Field", "colour", { alias: "lureo-page" }),
      t("lureo-field-line", "Field line", "colour", { alias: "lureo-line" }),
      t("lureo-field-line-width", "Field line weight", "length", { alias: "lureo-border-width" }),
      t("lureo-field-line-focus", "Field line on focus", "colour", { alias: "lureo-focus" }),
      t("lureo-field-placeholder", "Field placeholder", "colour", { alias: "lureo-ink-subtlest" }),
      t("lureo-tooltip", "Tooltip", "colour", { alias: "lureo-ink-strong" }),
      t("lureo-on-tooltip", "On tooltip", "colour", { alias: "lureo-surface" }),
      t("lureo-segment-on", "Chosen segment", "colour", { alias: "lureo-surface" }),
      t("lureo-on-segment-on", "On chosen segment", "colour", { alias: "lureo-ink" }),
      t("lureo-segmented-line", "Segmented tray line", "colour", { fixed: "oklch(0 0 0 / 0)" }),
      t("lureo-switch-off", "Switch off", "colour", { alias: "lureo-line-strong" }),
      t("lureo-switch-thumb", "Switch knob", "colour", { alias: "lureo-surface" }),
      t("lureo-switch-thumb-on", "Switch knob on", "colour", { alias: "lureo-switch-thumb" }),
      t("lureo-shadow-switch-off", "Switch off edge", "shadow", { fixed: "0 0 #0000" }),
      t("lureo-badge", "Badge", "colour", { alias: "lureo-brand" }),
      t("lureo-scrollbar", "Scrollbar", "colour", { alias: "lureo-ink-subtlest" }),
      t("lureo-button-ring", "Button ring", "colour", { alpha: ["lureo-line-strong", 0.5, null] }),
      t("lureo-tab-on", "Open tab", "colour", { alpha: ["lureo-emphasis", 0.1, null] }),
      t("lureo-on-tab-on", "On the open tab", "colour", { alias: "lureo-ink-strong" }),
      t("lureo-segmented-tray", "Segmented tray", "colour", { alpha: ["lureo-brand-ink", 0.06, null] }),
      t("lureo-switch-on", "Switch on", "colour", { alias: "lureo-brand" }),
      t("lureo-selected", "Selected", "colour", { alpha: ["lureo-brand-ink", 0.1, null] }),
      t("lureo-on-selected", "On selected", "colour", { alias: "lureo-brand-ink" }),
      t("lureo-selected-line", "Selected line", "colour", { alias: "lureo-brand-ink" }),
      t("lureo-tag-line", "Tag line", "colour", { alpha: ["lureo-emphasis-strong", 0.4, null] }),
      t("lureo-tag-line-width", "Tag line weight", "length", { alias: "lureo-border-width" }),
      t("lureo-inset", "Inset", "colour", { alpha: ["lureo-brand-ink", 0.04, null] }),
      t("lureo-inset-line", "Inset line", "colour", { alpha: ["lureo-brand-ink", 0.18, null] }),
      t("lureo-inset-line-width", "Inset line weight", "length", { alias: "lureo-border-width" }),
    ],
  },
  {
    id: "hero",
    label: "Hero band",
    describes: "The welcome band on the dashboard: a gradient through its three colours, in its direction, the last at 70%. Its glow and stripes fade with the sheen",
    tokens: [
      t("lureo-gradient-from", "Gradient from", "colour"),
      t("lureo-gradient-via", "Gradient via", "colour"),
      t("lureo-gradient-to", "Gradient to", "colour"),
      t("lureo-gradient-angle", "Gradient direction", "angle"),
      t("lureo-gradient-end", "Gradient end", "amount"),
    ],
  },
  {
    id: "plan",
    label: "Featured plan",
    describes: "The featured plan card's border and links",
    tokens: [
      t("lureo-plan-line", "Line", "colour", { mix: ["lureo-emphasis-strong", "lureo-secondary", 0.33] }),
      t("lureo-plan-link", "Link", "colour", { mix: ["lureo-sky", "lureo-accent", 0.4] }),
    ],
  },
  {
    id: "scene",
    label: "Editor and 3D",
    describes: "The 3D view as Unity draws it: its sky from top to bottom, its ground and the grid on the ground (each a solid colour), plus the timeline ruler's labels, the gizmo ring, handles, the bubble text style, preview tiles and checkerboards. The x, y and z axes are coral, mint and sky",
    tokens: [
      t("lureo-viewport-sky-top", "Sky top", "colour", { modes: [{ mix: ["lureo-surface", "lureo-accent", 0.23] }, { mix: ["lureo-surface", "lureo-ink-strong", 0.155] }] }),
      t("lureo-viewport-sky-bottom", "Sky bottom", "colour", { alias: "lureo-surface" }),
      t("lureo-viewport-floor", "Ground", "colour", { mix: ["lureo-surface", "lureo-ink-strong", [0.043, 0.056]] }),
      t("lureo-viewport-grid", "Grid", "colour", { mix: ["lureo-line", "lureo-sky", [0.12, 0.17]] }),
      t("lureo-viewport-grid-strong", "Grid strong", "colour", { mix: ["lureo-surface", "lureo-ink-strong", [0.39, 0.3]] }),
      t("lureo-timeline-ruler-ink", "Timeline ruler", "colour", { alias: "lureo-ink-subtle" }),
      t("lureo-timeline-clip", "Timeline clip", "colour", { alias: "lureo-backdrop-from" }),
      t("lureo-timeline-track", "Timeline track", "colour", { mix: ["lureo-surface", "lureo-ink-strong", [0.15, 0.12]] }),
      t("lureo-timeline-track-strong", "Timeline track, chosen", "colour", { mix: ["lureo-surface", "lureo-ink-strong", [0.3, 0.24]] }),
      t("lureo-timeline-track-faint", "Timeline track, hidden", "colour", { mix: ["lureo-surface", "lureo-ink-strong", [0.06, 0.06]] }),
      t("lureo-timeline-track-faint-strong", "Timeline track, hidden and chosen", "colour", { mix: ["lureo-surface", "lureo-ink-strong", [0.13, 0.1]] }),
      t("lureo-timeline-clip-line", "Timeline clip line", "colour", { alpha: ["lureo-emphasis", 0.7, null] }),
      t("lureo-timeline-clip-on", "Chosen clip", "colour", { alias: "lureo-brand" }),
      t("lureo-on-timeline-clip-on", "On chosen clip", "colour", { alias: "lureo-on-brand" }),
      t("lureo-timeline-playhead", "Playhead", "colour", { alias: "lureo-brand-ink-pressed" }),
      t("lureo-gizmo-ring", "Gizmo ring", "colour", { mix: ["lureo-mint", "lureo-on-emphasis", 0.28] }),
      t("lureo-handle", "Handle", "colour", { mix: ["lureo-on-emphasis", "lureo-shade", 0.13] }),
      t("lureo-style-bubble", "Bubble style", "colour", { mix: ["lureo-accent", "lureo-on-emphasis", 0.33] }),
      t("lureo-tile-shade", "Tile shade", "colour", { mix: ["lureo-emphasis", "lureo-on-emphasis", 0.21] }),
      t("lureo-checker-light", "Checker light", "colour", { mix: ["lureo-grey", "lureo-on-emphasis", 0.46] }),
      t("lureo-checker-dark", "Checker dark", "colour", { mix: ["lureo-grey", "lureo-grey-strong", 0.63] }),
    ],
  },
  {
    id: "map",
    label: "Map",
    describes: "The project map's land, water, parks, roads and labels: greys between the surface and the strongest ink",
    tokens: [
      t("lureo-map-land", "Land and places", "colour", { mix: ["lureo-surface", "lureo-ink-strong", 0.04] }),
      t("lureo-map-water", "Water", "colour", { mix: ["lureo-surface", "lureo-ink-strong", 0.22] }),
      t("lureo-map-park", "Park", "colour", { mix: ["lureo-surface", "lureo-ink-strong", 0.11] }),
      t("lureo-map-line", "Roads and outlines", "colour", { mix: ["lureo-surface", "lureo-ink-strong", 0.16] }),
      t("lureo-map-label", "Label", "colour", { mix: ["lureo-surface", "lureo-ink-strong", 0.68] }),
      t("lureo-map-water-label", "Water label", "colour", { mix: ["lureo-surface", "lureo-ink-strong", 0.41] }),
      t("lureo-map-parcel-label", "Parcel label", "colour", { mix: ["lureo-surface", "lureo-ink-strong", 0.27] }),
    ],
  },
  {
    id: "shadows",
    label: "Shadows",
    describes: "The shadows things cast, in black and stronger in a dark theme: a card on the page (raised, or modal when it stands further off), the landing page's video (raised, on a large soft drop as the theme turns to glass), a card in a card, floating panels and menus, header bars, dialogs, small controls, the sign-in card and dragged items. Also buttons' own shadow (the call to action's and outlined buttons' apart, each also under the pointer, and any button's while pressed; 0 0 #0000 for none), the open tab's (an underline, or a raised card), a focused field's ring, and the shadow behind page titles' letters (none for none).. A card that does something takes its own shadow under the pointer",
    tokens: [
      t("lureo-shadow-raised", "Raised", "shadow", { css: "0 4px 24px color-mix(in oklab, color-mix(in oklab, var(--lureo-shade) 10%, transparent), color-mix(in oklab, var(--lureo-accent) 10%, transparent) calc(var(--lureo-sheen) * 100%))" }),
      t("lureo-shadow-float", "Float", "shadow", { css: "var(--lureo-shadow-raised), 0 16px 48px color-mix(in oklab, var(--lureo-shade) calc(calc(0.12 * (1 + 4.62 * var(--lureo-dark)) * var(--lureo-glass)) * 100%), transparent)" }),
      t("lureo-shadow-overlay", "Overlay", "shadow", { css: "0 1px 10px 1px color-mix(in oklab, var(--lureo-shade) 17.5%, transparent), inset 0 1px 0 color-mix(in oklab, var(--lureo-highlight) calc(calc((1 - var(--lureo-glass)) * (1 - 0.92 * var(--lureo-dark))) * 100%), transparent), 0 0 0 1px color-mix(in oklab, var(--lureo-line) calc((1 - var(--lureo-glass)) * 100%), transparent)" }),
      t("lureo-shadow-bar", "Bar", "shadow", { css: "0 8px 32px color-mix(in oklab, var(--lureo-shade) calc(calc(0.08 * (1 + 4.62 * var(--lureo-dark))) * 100%), transparent), 0 0 0 1px color-mix(in oklab, var(--lureo-highlight) calc(calc(0.1 * var(--lureo-glass)) * 100%), transparent)" }),
      t("lureo-shadow-modal", "Modal", "shadow", { css: "0 16px 48px color-mix(in oklab, var(--lureo-shade) calc(calc(0.12 * (1 + 4.62 * var(--lureo-dark))) * 100%), transparent), 0 0 0 1px color-mix(in oklab, var(--lureo-highlight) calc(calc(0.15 * var(--lureo-glass)) * 100%), transparent)" }),
      t("lureo-shadow-lift", "Lift", "shadow", { css: "0 12px 40px color-mix(in oklab, color-mix(in oklab, var(--lureo-shade) 15%, transparent), color-mix(in oklab, var(--lureo-accent) 15%, transparent) calc(var(--lureo-sheen) * 100%))" }),
      t("lureo-shadow-lift-hover", "Lift hover", "shadow", { alias: "lureo-shadow-lift" }),
      t("lureo-shadow-card-hover", "Card hover", "shadow", { alias: "lureo-shadow-raised" }),
      t("lureo-shadow-sheet", "Sheet", "shadow", { css: "inset 0 1px 0 color-mix(in oklab, var(--lureo-highlight) calc(calc((1 - var(--lureo-glass)) * (1 - 0.92 * var(--lureo-dark))) * 100%), transparent), 0 0 0 1px color-mix(in oklab, var(--lureo-line) calc((1 - var(--lureo-glass)) * 100%), transparent), 0 0 19px 1.5px color-mix(in oklab, var(--lureo-shade) calc(calc(0.11 * (1 - var(--lureo-glass))) * 100%), transparent)" }),
      t("lureo-shadow-control", "Control", "shadow", { css: "0 1px 2px color-mix(in oklab, var(--lureo-emphasis) 8%, transparent)" }),
      t("lureo-shadow-button", "Button", "shadow", { fixed: "0 0 #0000" }),
      t("lureo-shadow-cta", "Call to action", "shadow", { fixed: "0 0 #0000" }),
      t("lureo-shadow-outline-button", "Outline button", "shadow", { fixed: "0 0 #0000" }),
      t("lureo-shadow-button-pressed", "Button pressed", "shadow", { fixed: "0 0 #0000" }),
      t("lureo-shadow-button-hover", "Button hover", "shadow", { fixed: "0 0 #0000" }),
      t("lureo-shadow-outline-button-hover", "Outline button hover", "shadow", { fixed: "0 0 #0000" }),
      t("lureo-shadow-cta-hover", "Call to action hover", "shadow", { fixed: "0 0 #0000" }),
      t("lureo-shadow-tab", "Open tab", "shadow", { alias: "lureo-shadow-control" }),
      t("lureo-shadow-cta-pressed", "Call to action pressed", "shadow", { alias: "lureo-shadow-button-pressed" }),
      t("lureo-shadow-outline-button-pressed", "Outline button pressed", "shadow", { alias: "lureo-shadow-button-pressed" }),
      t("lureo-shadow-solid", "Solid button", "shadow", { alias: "lureo-shadow-button" }),
      t("lureo-shadow-solid-hover", "Solid button hover", "shadow", { alias: "lureo-shadow-button-hover" }),
      t("lureo-shadow-solid-pressed", "Solid button pressed", "shadow", { alias: "lureo-shadow-button-pressed" }),
      t("lureo-shadow-danger", "Danger button", "shadow", { alias: "lureo-shadow-button" }),
      t("lureo-shadow-danger-hover", "Danger button hover", "shadow", { alias: "lureo-shadow-button-hover" }),
      t("lureo-shadow-danger-pressed", "Danger button pressed", "shadow", { alias: "lureo-shadow-button-pressed" }),
      t("lureo-shadow-segment-on", "Chosen segment", "shadow", { alias: "lureo-shadow-control" }),
      t("lureo-shadow-panel-card", "Card in a panel", "shadow", { fixed: "0 0 #0000" }),
      t("lureo-field-focus", "Field focus", "shadow", { css: "0 0 0 2px color-mix(in oklab, var(--lureo-accent) 70%, transparent)" }),
      t("lureo-title-shadow", "Title shadow", "shadow", { fixed: "none" }),
    ],
  },
  {
    id: "effects",
    label: "Effects",
    describes: "Sheen is how strongly glows and shines show (0 turns them off). Photo shade and brightness are the shade over card photos and how bright the photos themselves are (1 as they are, 0.9 dimmed); photo veil is a colour laid evenly over them (clear in Lureo, a faint white in some dark sets). Glass is how much the lit rim of header bars shows on a dark set (1 in full, 0 faintly); the bars' colour, the blurs and the card edges are tokens of their own",
    tokens: [
      t("lureo-sheen", "Sheen", "amount"),
      t("lureo-photo-shade", "Photo shade", "amount"),
      t("lureo-photo-shade-tint", "Photo shade colour", "colour", { alias: "lureo-emphasis" }),
      t("lureo-photo-brightness", "Photo brightness", "scale", { fixed: "1" }),
      t("lureo-photo-hover-brightness", "Photo brightness on hover", "scale", { fixed: "1" }),
      t("lureo-photo-veil", "Photo veil", "colour", { fixed: "oklch(0 0 0 / 0)" }),
      t("lureo-glass", "Glass", "amount"),
      t("lureo-blur-sm", "Blur small", "length", { css: "calc(var(--lureo-glass) * 4px)" }),
      t("lureo-blur-lg", "Blur large", "length", { css: "calc(var(--lureo-glass) * 16px)" }),
    ],
  },
  {
    id: "shape",
    label: "Shape",
    describes: "Corners from small to a full pill (9999px), button corners (primary buttons half as round again) and pill buttons' corners, tags' corners, tabs' corners (each corner can differ: 8px 8px 0 0 rounds only the top), chips', menus', dialogs' and fields' corners, and line widths from a hairline to heavy",
    tokens: [
      t("lureo-radius", "Roundness", "length"),
      t("lureo-radius-control", "Button corners", "length"),
      t("lureo-radius-button-pill", "Pill button corners", "length"),
      t("lureo-border-width", "Line weight", "length"),
      t("lureo-radius-sm", "Corner small", "length", { css: "calc(var(--lureo-radius) * 2 / 3)" }),
      t("lureo-radius-lg", "Corner large", "length", { css: "calc(var(--lureo-radius) * 4 / 3)" }),
      t("lureo-radius-xl", "Corner extra large", "length", { css: "calc(var(--lureo-radius) * 2)" }),
      t("lureo-radius-pill", "Pill", "length", { css: "calc(var(--lureo-radius-xl) + min(var(--lureo-radius) * 99999, 9999px))" }),
      t("lureo-radius-control-lg", "Primary button corners", "length", { css: "calc(var(--lureo-radius-control) * 1.5)" }),
      t("lureo-radius-tag", "Tag", "length", { alias: "lureo-radius-pill" }),
      t("lureo-radius-tab", "Tab corners", "length", { css: "calc(var(--lureo-radius) * 2 / 3)" }),
      t("lureo-radius-tooltip", "Tooltip corners", "length", { alias: "lureo-radius" }),
      t("lureo-radius-avatar", "Avatar corners", "length", { fixed: "9999px" }),
      t("lureo-radius-segmented", "Segmented corners", "length", { alias: "lureo-radius-pill" }),
      t("lureo-radius-chip", "Chip corners", "length", { fixed: "9999px" }),
      t("lureo-radius-menu", "Menu corners", "length", { alias: "lureo-radius" }),
      t("lureo-radius-dialog", "Dialog corners", "length", { css: "calc(var(--lureo-radius) * 4 / 3)" }),
      t("lureo-radius-field", "Field corners", "length", { alias: "lureo-radius" }),
      t("lureo-border-width-medium", "Medium line", "length", { css: "calc(var(--lureo-border-width) * 1.5)" }),
      t("lureo-border-width-strong", "Strong line", "length", { css: "calc(var(--lureo-border-width) * 2)" }),
      t("lureo-border-width-heavy", "Heavy line", "length", { css: "calc(var(--lureo-border-width) * 3)" }),
      t("lureo-line-style", "Line style", "line", { fixed: "solid" }),
    ],
  },
  {
    id: "motion",
    label: "Motion",
    describes: "How long things take to move and how they speed up and slow down: five durations from fast to slowest, the sweep a meter's sheen takes across it, a standard curve for things that stay on screen, an enter curve for things that arrive and an exit curve for things that leave; how a pressed button shrinks (1 not at all) and how far it drops, how far a button moves under the pointer (up and down, negative lifts it, and sideways) and how much it grows, how much the New Story hero button shrinks while pressed, and how much a card you can open grows under the pointer",
    tokens: [
      t("lureo-duration-fast", "Fast", "duration", { fixed: "150ms" }),
      t("lureo-duration-moderate", "Moderate", "duration", { fixed: "200ms" }),
      t("lureo-duration-slow", "Slow", "duration", { fixed: "300ms" }),
      t("lureo-duration-button", "Button", "duration", { fixed: "300ms" }),
      t("lureo-duration-slower", "Slower", "duration", { fixed: "500ms" }),
      t("lureo-duration-slowest", "Slowest", "duration", { fixed: "700ms" }),
      t("lureo-duration-sweep", "Sweep", "duration", { fixed: "1200ms" }),
      t("lureo-backdrop-drift", "Backdrop drift", "duration", { fixed: "0s" }),
      t("lureo-easing-standard", "Standard", "easing", { fixed: "cubic-bezier(0.4, 0, 0.2, 1)" }),
      t("lureo-easing-enter", "Enter", "easing", { fixed: "cubic-bezier(0, 0, 0.2, 1)" }),
      t("lureo-easing-exit", "Exit", "easing", { fixed: "cubic-bezier(0.4, 0, 1, 1)" }),
      t("lureo-press-scale", "Press scale", "scale", { fixed: "1" }),
      t("lureo-card-hover-scale", "Card hover scale", "scale", { fixed: "1.02" }),
      t("lureo-photo-hover-scale", "Photo zoom on hover", "scale", { fixed: "1" }),
      t("lureo-hero-press-scale", "Hero button press scale", "scale", { fixed: "0.995" }),
      t("lureo-press-offset", "Press drop", "length", { fixed: "0px" }),
      t("lureo-hover-offset", "Hover lift", "length", { fixed: "0px" }),
      t("lureo-hover-offset-x", "Hover shift", "length", { fixed: "0px" }),
      t("lureo-hover-scale", "Hover scale", "scale", { fixed: "1" }),
    ],
  },
  {
    id: "layers",
    label: "Layers",
    describes: "What sits over what, lowest first: bars, then panels, menus and dialogs, tooltips, full-screen blockers, tours, and toasts over everything",
    tokens: [
      t("lureo-layer-bar", "Bar", "layer", { fixed: "40" }),
      t("lureo-layer-overlay", "Overlay", "layer", { fixed: "50" }),
      t("lureo-layer-tooltip", "Tooltip", "layer", { fixed: "110" }),
      t("lureo-layer-blocker", "Blocker", "layer", { fixed: "9999" }),
      t("lureo-layer-tour", "Tour", "layer", { fixed: "10000" }),
      t("lureo-layer-toast", "Toast", "layer", { fixed: "10010" }),
    ],
  },
  {
    id: "type",
    label: "Type",
    describes: "The font for interface text, the font for descriptive copy (paragraphs, hints, subtitles and empty states), the font for code and the font for display and page titles, which the app loads whichever they name; the weight of text, of titles (card and section titles a step lighter), of button labels and of interface labels (menu items, tab names, the editor's panels and controls); the case and letter spacing of button, tab and segmented labels; the letter spacing of all other text; the case, letter spacing, style (upright or italic) and line height (1 as Lureo sets each title, less is tighter) of titles, and the width, first-line indent and fill (a gradient, or none) of display and page titles, and the rule above page titles (its width, 0px for none, and ink); the weight, ink and letter spacing of overlines (the small capitals over sections and fields, a newspaper's kickers); and the OpenType features all text uses",
    tokens: [
      t("lureo-font-ui", "Text", "font"),
      t("lureo-font-prose", "Prose", "font"),
      t("lureo-font-mono", "Mono", "font"),
      t("lureo-font-display", "Display", "font"),
      t("lureo-weight-text", "Text weight", "weight"),
      t("lureo-weight-heading", "Title weight", "weight"),
      t("lureo-label-case", "Label case", "case"),
      t("lureo-tab-case", "Tab case", "case", { fixed: "none" }),
      t("lureo-title-case", "Title case", "case"),
      t("lureo-label-tracking", "Label spacing", "length"),
      t("lureo-text-tracking", "Text spacing", "length", { fixed: "0em" }),
      t("lureo-font-features", "Font features", "features"),
      t("lureo-weight-subheading", "Subtitle weight", "weight", { css: "max(100, calc(var(--lureo-weight-heading) - 100))" }),
      t("lureo-weight-button", "Button weight", "weight", { fixed: "600" }),
      t("lureo-weight-label", "Label weight", "weight", { fixed: "500" }),
      t("lureo-title-tracking", "Title spacing", "length", { fixed: "0em" }),
      t("lureo-title-style", "Title style", "style", { fixed: "normal" }),
      t("lureo-label-style", "Label style", "style", { fixed: "normal" }),
      t("lureo-label-shadow", "Label shadow", "shadow", { fixed: "none" }),
      t("lureo-title-flicker", "Title flicker", "duration", { fixed: "0s" }),
      t("lureo-title-leading", "Title leading", "scale", { fixed: "1" }),
      t("lureo-title-width", "Title width", "width", { fixed: "100%" }),
      t("lureo-title-indent", "Title indent", "length", { fixed: "0em" }),
      t("lureo-title-fill", "Title fill", "gradient", { fixed: "none" }),
      t("lureo-title-rule", "Title rule", "length", { fixed: "0px" }),
      t("lureo-title-rule-ink", "Title rule ink", "colour", { alias: "lureo-ink-strong" }),
      t("lureo-weight-overline", "Overline weight", "weight", { fixed: "300" }),
      t("lureo-overline-case", "Overline case", "case", { fixed: "uppercase" }),
      t("lureo-font-label", "Label font", "font", { fixed: "ui-sans-serif, system-ui, sans-serif" }),
      t("lureo-overline-ink", "Overline ink", "colour", { alias: "lureo-ink-subtle" }),
      t("lureo-overline-tracking", "Overline spacing", "length", { fixed: "0.15em" }),
    ],
  },
  {
    id: "texture",
    label: "Texture",
    describes: "A pattern laid over the page and the hero band, how strong it is, and how big its tile is shown",
    tokens: [
      t("lureo-texture-pattern", "Pattern", "pattern"),
      t("lureo-texture-strength", "Strength", "amount"),
      t("lureo-texture-size", "Size", "length"),
    ],
  },
  {
    id: "logo",
    label: "Logo",
    describes: "Lureo's logo as its artwork, or its shape drawn in the brand ink",
    tokens: [
      t("lureo-logo-style", "Style", "logo"),
    ],
  },
];

/** Every token, group by group. */
export const TOKENS: readonly TokenSpec[] = TOKEN_GROUPS.flatMap(group => group.tokens);

/** The tokens with no formula: the generator draws them for a new set. */
export const BASE_TOKENS: readonly TokenSpec[] = TOKENS.filter(token => !token.derive);

/** The tokens with a formula, Dark among them. */
export const DERIVED_TOKENS: readonly TokenSpec[] = TOKENS.filter(token => token.derive);

/** How many tokens there are: the one count every page states. */
export const TOKEN_COUNT = TOKENS.length;

/** Every token a token set holds a value for: all but Dark, which is 1 in a dark set and 0 in a light one. */
export const SET_TOKENS: readonly TokenSpec[] = TOKENS.filter(token => !(token.derive && 'mode' in token.derive));

/**
 * Tokens that came after sets were made, each with the token whose value a set saved before it starts with, as a copy:
 * links its brand ink, the text on its brand fill the text on its deep fills, prose and display its interface face, button
 * corners its large corner and pill buttons its button corners, the hero band its emphasis into its secondary, and the controls'
 * text, fills, lines and corners their nearest token. The rest
 * a set lacks come from the default Light or Dark.
 */
export const STARTS_AS: Readonly<Record<string, string>> = {
  'lureo-link': 'lureo-brand-ink',
  'lureo-page-ink-strong': 'lureo-ink-strong',
  'lureo-page-ink': 'lureo-ink',
  'lureo-page-ink-subtle': 'lureo-ink-subtle',
  'lureo-page-ink-subtlest': 'lureo-ink-subtlest',
  'lureo-on-brand': 'lureo-on-emphasis',
  'lureo-font-prose': 'lureo-font-ui',
  'lureo-font-display': 'lureo-font-ui',
  'lureo-radius-control': 'lureo-radius-lg',
  'lureo-radius-button-pill': 'lureo-radius-control',
  'lureo-gradient-from': 'lureo-emphasis',
  'lureo-gradient-via': 'lureo-secondary',
  'lureo-gradient-to': 'lureo-secondary',
  // The tokens for controls' own text, fills, lines and corners, from the set's nearest values, so a set saved before them reads
  'lureo-bar-ink': 'lureo-ink-strong',
  'lureo-on-tab-on': 'lureo-ink-strong',
  'lureo-title-rule-ink': 'lureo-ink-strong',
  'lureo-focus': 'lureo-brand-ink',
  'lureo-on-chip-on': 'lureo-brand-ink',
  'lureo-chip-on-line': 'lureo-brand-ink',
  'lureo-on-selected': 'lureo-brand-ink',
  'lureo-selected-line': 'lureo-brand-ink',
  'lureo-chip': 'lureo-surface',
  'lureo-on-chip': 'lureo-ink',
  'lureo-chip-line': 'lureo-line',
  'lureo-chip-on': 'lureo-hover',
  'lureo-selected': 'lureo-hover',
  'lureo-segmented-tray': 'lureo-hover',
  'lureo-tab-on': 'lureo-hover',
  'lureo-inset': 'lureo-hover',
  'lureo-link-hover': 'lureo-link',
  'lureo-page-link': 'lureo-link',
  'lureo-page-link-hover': 'lureo-link-hover',
  'lureo-on-secondary': 'lureo-on-emphasis',
  'lureo-on-cta-soft': 'lureo-on-cta',
  'lureo-switch-on': 'lureo-brand',
  'lureo-overline-ink': 'lureo-ink-subtle',
  'lureo-on-disabled': 'lureo-ink-subtlest',
  'lureo-inset-line': 'lureo-line',
  'lureo-tag-line': 'lureo-line-strong',
  'lureo-radius-tab': 'lureo-radius-sm',
  'lureo-radius-menu': 'lureo-radius',
  'lureo-radius-field': 'lureo-radius',
  'lureo-radius-dialog': 'lureo-radius-lg',
  'lureo-tag-line-width': 'lureo-border-width',
  'lureo-inset-line-width': 'lureo-border-width',
  'lureo-shadow-tab': 'lureo-shadow-control',
  'lureo-viewport-sky-bottom': 'lureo-surface',
  'lureo-viewport-floor': 'lureo-neutral',
  'lureo-on-secondary-hover': 'lureo-on-secondary',
  'lureo-on-secondary-pressed': 'lureo-on-secondary',
  'lureo-shadow-lift-hover': 'lureo-shadow-lift',
  'lureo-cta-label-hover': 'lureo-on-cta',
  'lureo-cta-label-pressed': 'lureo-on-cta',
  'lureo-chip-hover': 'lureo-chip',
  'lureo-on-chip-hover': 'lureo-on-chip',
  'lureo-chip-hover-line': 'lureo-brand-ink',
  'lureo-on-outline-hover': 'lureo-brand-ink',
  'lureo-outline-line-hover': 'lureo-brand-ink',
  'lureo-card-hover-line': 'lureo-brand-ink',
  'lureo-shadow-card-hover': 'lureo-shadow-raised',
  'lureo-chrome': 'lureo-backdrop',
  'lureo-link-decoration-hover': 'lureo-link-decoration',
  'lureo-link-visited': 'lureo-link',
  'lureo-link-pressed': 'lureo-link-hover',
  'lureo-card-hover': 'lureo-surface',
  'lureo-chip-on-hover': 'lureo-chip-on',
  'lureo-chip-line-width': 'lureo-border-width-medium',
  'lureo-upload-line-width': 'lureo-border-width-strong',
  'lureo-photo-hover-brightness': 'lureo-photo-brightness',
  'lureo-tab-case': 'lureo-label-case',
  'lureo-menu': 'lureo-surface',
  'lureo-dialog': 'lureo-page',
  'lureo-popover': 'lureo-menu',
  'lureo-on-menu': 'lureo-ink',
  'lureo-on-menu-subtle': 'lureo-ink-subtle',
  'lureo-on-menu-subtlest': 'lureo-ink-subtlest',
  'lureo-menu-hover': 'lureo-hover',
  'lureo-on-menu-hover': 'lureo-on-menu',
  'lureo-menu-line': 'lureo-line',
  'lureo-switch-thumb-on': 'lureo-switch-thumb',
  'lureo-field': 'lureo-page',
  'lureo-field-line': 'lureo-line',
  'lureo-field-line-width': 'lureo-border-width',
  'lureo-field-line-focus': 'lureo-focus',
  'lureo-field-placeholder': 'lureo-ink-subtlest',
  'lureo-tooltip': 'lureo-ink-strong',
  'lureo-on-tooltip': 'lureo-surface',
  'lureo-segment-on': 'lureo-surface',
  'lureo-on-segment-on': 'lureo-ink',
  'lureo-switch-off': 'lureo-line-strong',
  'lureo-switch-thumb': 'lureo-surface',
  'lureo-badge': 'lureo-brand',
  'lureo-scrollbar': 'lureo-ink-subtlest',
  'lureo-radius-tooltip': 'lureo-radius',
  'lureo-shadow-cta-pressed': 'lureo-shadow-button-pressed',
  'lureo-shadow-outline-button-pressed': 'lureo-shadow-button-pressed',
  'lureo-shadow-solid': 'lureo-shadow-button',
  'lureo-shadow-solid-hover': 'lureo-shadow-button-hover',
  'lureo-shadow-solid-pressed': 'lureo-shadow-button-pressed',
  'lureo-shadow-danger': 'lureo-shadow-button',
  'lureo-shadow-danger-hover': 'lureo-shadow-button-hover',
  'lureo-shadow-danger-pressed': 'lureo-shadow-button-pressed',
  'lureo-shadow-segment-on': 'lureo-shadow-control',
  'lureo-editor-bar': 'lureo-chrome',
  'lureo-radius-segmented': 'lureo-radius-pill',
  'lureo-duration-button': 'lureo-duration-slow',
  'lureo-font-label': 'lureo-font-ui',
  'lureo-timeline-clip': 'lureo-backdrop-from',
  'lureo-timeline-clip-on': 'lureo-brand',
  'lureo-on-timeline-clip-on': 'lureo-on-brand',
  'lureo-timeline-playhead': 'lureo-brand-ink-pressed',
  'lureo-timeline-ruler-ink': 'lureo-ink-subtle',
};

const byName = new Map(TOKENS.map(token => [token.name, token]));

/** A token by name. */
export const tokenSpec = (name: string): TokenSpec | undefined => byName.get(name);

/** A token's CSS custom property: --<name>, lureo- like every token's name, so Lureo's variables never meet another library's. */
export const cssVariable = (name: string) => `--${name}`;

/** A token read in CSS: var(--lureo-brand). */
export const cssVar = (name: string) => `var(${cssVariable(name)})`;

// The utility a token fills by its name, over its kind's: a corner, a line width, a blur, a fade, a press, a title's shadow
const LENGTH_UTILITIES: Record<string, string> = {
  'lureo-radius-sm': 'rounded',
  'lureo-radius': 'rounded',
  'lureo-radius-lg': 'rounded',
  'lureo-radius-xl': 'rounded',
  'lureo-radius-pill': 'rounded',
  'lureo-radius-control': 'rounded',
  'lureo-radius-control-lg': 'rounded',
  'lureo-radius-button-pill': 'rounded',
  'lureo-radius-tag': 'rounded',
  'lureo-radius-tab': 'rounded',
  'lureo-radius-tooltip': 'rounded',
  'lureo-radius-avatar': 'rounded',
  'lureo-radius-segmented': 'rounded',
  'lureo-radius-chip': 'rounded',
  'lureo-radius-menu': 'rounded',
  'lureo-radius-dialog': 'rounded',
  'lureo-radius-field': 'rounded',
  'lureo-label-tracking': 'tracking',
  'lureo-title-tracking': 'tracking',
  'lureo-text-tracking': 'tracking',
  'lureo-overline-tracking': 'tracking',
  'lureo-title-indent': 'indent',
  'lureo-border-width': 'border',
  'lureo-border-width-medium': 'border',
  'lureo-border-width-strong': 'border',
  'lureo-border-width-heavy': 'border',
  'lureo-tag-line-width': 'border',
  'lureo-chip-line-width': 'border',
  'lureo-upload-line-width': 'border',
  'lureo-field-line-width': 'border',
  'lureo-inset-line-width': 'border',
  'lureo-blur-sm': 'backdrop-blur',
  'lureo-blur-lg': 'backdrop-blur',
  'lureo-disabled-opacity': 'opacity',
  'lureo-sheen': 'opacity',
  'lureo-photo-shade': 'opacity',
  'lureo-press-scale': 'scale',
  'lureo-card-hover-scale': 'scale',
  'lureo-hero-press-scale': 'scale',
  'lureo-photo-hover-scale': 'scale',
  'lureo-photo-hover-brightness': 'brightness',
  'lureo-link-underline-offset': 'underline-offset',
  'lureo-photo-brightness': 'brightness',
  'lureo-press-offset': 'translate-y',
  'lureo-hover-scale': 'scale',
  'lureo-title-shadow': 'text-shadow',
  'lureo-label-shadow': 'text-shadow',
};
// The utility every token of a kind fills
const KIND_UTILITIES: Partial<Record<TokenKind, string>> = { colour: 'bg', gradient: 'bg', shadow: 'shadow', duration: 'duration', easing: 'ease', layer: 'z', font: 'font', weight: 'font', case: 'case', width: 'font-width', decoration: 'decoration', style: 'font-style', line: 'line-style' };

/** The Tailwind utility a token fills, and its key there, the token's name: bg-lureo-brand, ease-lureo-easing-exit, rounded-lureo-radius. None for a token only formulas read. */
export function tokenUtility(name: string): { utility: string; key: string } | undefined {
  const spec = byName.get(name);
  const utility = spec && (LENGTH_UTILITIES[name] ?? KIND_UTILITIES[spec.kind]);
  return utility ? { utility, key: name } : undefined;
}

/** The class a token makes (a colour's fill): bg-lureo-brand, z-lureo-layer-overlay. */
export const tokenClass = (name: string) => {
  const made = tokenUtility(name);
  return made && `${made.utility}-${made.key}`;
};

const trim = (n: number) => String(Number(n.toFixed(4)));
const percent = (n: number) => `${trim(n * 100)}%`;

// A mix's amount as a CSS percentage: fixed, between its light and dark amounts by --lureo-dark, or an amount token's
function amountCss(amount: MixAmount): string {
  if (typeof amount === 'number') return percent(amount);
  if (typeof amount === 'string') return `calc(${cssVar(amount)} * 100%)`;
  const [light, dark] = amount;
  return `calc(${percent(light)} + ${percent(dark - light)} * ${cssVar('lureo-dark')})`;
}

/** A derived token's CSS value; the theme's darkness has none here, as each theme writes its own. */
export function deriveCss(derive: Derive): string {
  if ('alias' in derive) return cssVar(derive.alias);
  if ('mix' in derive) {
    const [from, to, amount] = derive.mix;
    return `color-mix(in oklab, ${cssVar(from)}, ${cssVar(to)} ${amountCss(amount)})`;
  }
  if ('alpha' in derive) {
    const [name, opacity, scaledBy, offset] = derive.alpha;
    const alpha = !scaledBy
      ? String(opacity)
      : offset
        ? `calc(${offset} ${opacity < 0 ? '-' : '+'} ${Math.abs(opacity)} * ${cssVar(scaledBy)})`
        : opacity === 1
          ? cssVar(scaledBy)
          : `calc(${opacity} * ${cssVar(scaledBy)})`;
    return `color-mix(in oklab, ${cssVar(name)} calc(${alpha} * 100%), transparent)`;
  }
  if ('shift' in derive) {
    const [name, by, scaledBy, flips] = derive.shift;
    const amount = [scaledBy ? cssVar(scaledBy) : '', flips ? `(1 - 2 * ${cssVar('lureo-dark')})` : ''].filter(Boolean).join(' * ');
    const move = (channel: 'l' | 'c' | 'h') => {
      const delta = by[channel];
      if (!delta) return channel;
      const moved = `calc(${channel} ${delta < 0 ? '-' : '+'} ${Math.abs(delta)}${amount ? ` * ${amount}` : ''})`;
      return channel === 'c' ? `max(0, ${moved})` : moved;
    };
    return `oklch(from ${cssVar(name)} ${move('l')} ${move('c')} ${move('h')})`;
  }
  if ('fixed' in derive) return derive.fixed;
  if ('modes' in derive) return `color-mix(in oklab, ${deriveCss(derive.modes[0])}, ${deriveCss(derive.modes[1])} calc(${cssVar('lureo-dark')} * 100%))`;
  if ('mode' in derive) return '';
  return derive.css;
}

const amountText = (amount: MixAmount) =>
  typeof amount === 'number' ? percent(amount) : typeof amount === 'string' ? `${amount} × 100%` : `${percent(amount[0])} (light) or ${percent(amount[1])} (dark)`;

/** How a derived token is made, in words (brand-ink mixed with 31% ink-strong). */
export function deriveText(derive: Derive): string {
  if ('alias' in derive) return `= ${derive.alias}`;
  if ('mix' in derive) return `${derive.mix[0]} mixed with ${amountText(derive.mix[2])} ${derive.mix[1]}`;
  if ('alpha' in derive) {
    const [name, opacity, scaledBy, offset] = derive.alpha;
    if (!scaledBy) return `${name} at ${opacity}`;
    if (offset) return `${name} at ${offset} ${opacity < 0 ? '−' : '+'} ${Math.abs(opacity)} × ${scaledBy}`;
    return `${name} at ${opacity === 1 ? '' : `${opacity} × `}${scaledBy}`;
  }
  if ('shift' in derive) {
    const [name, by, scaledBy, flips] = derive.shift;
    const names = { l: 'lightness', c: 'chroma', h: 'hue' } as const;
    const moves = (['l', 'c', 'h'] as const).filter(channel => by[channel]).map(channel => `${names[channel]} ${by[channel]! < 0 ? '−' : '+'}${Math.abs(by[channel]!)}`);
    return `${name}, ${moves.join(', ')}${scaledBy ? ` × ${scaledBy}` : ''}${flips ? ' (the other way in a dark theme)' : ''}`;
  }
  if ('fixed' in derive) return `= ${derive.fixed}`;
  if ('modes' in derive) return `${deriveText(derive.modes[0])} (light); ${deriveText(derive.modes[1])} (dark)`;
  if ('mode' in derive) return '1 in a dark theme, 0 in a light one';
  return derive.css;
}

/** The tokens a derived token is made from. */
export function deriveSources(derive: Derive): string[] {
  if ('alias' in derive) return [derive.alias];
  if ('mix' in derive) {
    const [from, to, amount] = derive.mix;
    return [from, to, ...(typeof amount === 'string' ? [amount] : typeof amount === 'number' ? [] : ['lureo-dark'])];
  }
  if ('alpha' in derive) return [derive.alpha[0], ...(derive.alpha[2] ? [derive.alpha[2]] : [])];
  if ('shift' in derive) return [derive.shift[0], ...(derive.shift[2] ? [derive.shift[2]] : []), ...(derive.shift[3] ? ['lureo-dark'] : [])];
  if ('fixed' in derive || 'mode' in derive) return [];
  if ('modes' in derive) return [...new Set([...deriveSources(derive.modes[0]), ...deriveSources(derive.modes[1]), 'lureo-dark'])];
  return [...derive.css.matchAll(/var\(--([\w-]+)\)/g)].map(match => match[1]);
}
